import React from 'react';
import { Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { Drone } from '../../types';
import { degreesToCompass, formatDistance, formatSpeed } from '../../utils/helpers';

function createDroneIcon(drone: Drone, isSelected: boolean) {
  const color = isSelected ? '#ff5252' : sensorColor(drone.sensorType);
  const size = isSelected ? 36 : 28;
  const dir = drone.directionDeg || 0;
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <g transform="rotate(${dir}, 20, 20)" filter="url(#glow)">
        <polygon points="20,5 27,30 20,25 13,30" fill="${color}" opacity="0.9"/>
        <ellipse cx="10" cy="20" rx="9" ry="4" fill="${color}" opacity="0.6"/>
        <ellipse cx="30" cy="20" rx="9" ry="4" fill="${color}" opacity="0.6"/>
        <polygon points="20,2 23,10 17,10" fill="white" opacity="0.8"/>
      </g>
      <circle cx="20" cy="20" r="18" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.4"/>
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

function sensorColor(type?: string): string {
  switch (type) {
    case 'CRPC':     return '#4fc3f7';
    case 'DRONE_ID': return '#aed581';
    case 'RID':      return '#ffb74d';
    case 'CAMERA':   return '#f48fb1';
    case 'WIDEBAND': return '#ce93d8';
    default:         return '#90a4ae';
  }
}

function fmtFreq(mhz?: number | null): string {
  if (!mhz) return '—';
  return mhz >= 1000 ? `${(mhz / 1000).toFixed(2)} GHz` : `${mhz.toFixed(0)} MHz`;
}

interface Props {
  drone: Drone;
  onClick: () => void;
}

export default function DroneMarker({ drone, onClick }: Props) {
  if (!drone.lat || !drone.lng) return null;
  const icon = createDroneIcon(drone, false);

  return (
    <Marker
      position={[drone.lat, drone.lng]}
      icon={icon}
      eventHandlers={{ click: onClick }}
    >
      {/* Tooltip — показывается при наведении */}
      <Tooltip permanent={false} direction="top" offset={[0, -14]}>
        <div style={{ fontSize: 12, color: '#333', lineHeight: '1.6' }}>
          <strong>{drone.model || drone.droneId}</strong><br />
          Alt: {drone.altitudeM?.toFixed(0) ?? '—'}m &nbsp;·&nbsp; {formatSpeed(drone.speedMs)}<br />
          Dir: {drone.directionDeg?.toFixed(0) ?? '—'}° ({degreesToCompass(drone.directionDeg)})<br />
          Freq: {fmtFreq(drone.frequencyMhz)} &nbsp;·&nbsp; Signal: {drone.signalDb?.toFixed(0) ?? '—'} dB
        </div>
      </Tooltip>

      {/* Popup — показывается при клике (полная карточка цели) */}
      <Popup minWidth={220}>
        <div style={{ fontSize: 13, lineHeight: '1.8' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            🎯 �{drone.model || drone.droneId}
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <tr><td style={{ color: '#888', paddingRight: 8 }}>ID</td><td><code style={{ fontSize: 11 }}>{drone.droneId}</code></td></tr>
              <tr><td style={{ color: '#888' }}>Sensor</td><td>{drone.sensorType || '—'}</td></tr>
              <tr><td style={{ color: '#888' }}>Lat / Lng</td><td>{drone.lat?.toFixed(5)}, {drone.lng?.toFixed(5)}</td></tr>
              <tr><td style={{ color: '#888' }}>Altitude</td><td>{drone.altitudeM?.toFixed(1) ?? '—'} m</td></tr>
              <tr><td style={{ color: '#888' }}>Speed</td><td>{formatSpeed(drone.speedMs)}</td></tr>
              <tr><td style={{ color: '#888' }}>Heading</td><td>{drone.directionDeg?.toFixed(1) ?? '—'}º ({degreesToCompass(drone.directionDeg)})</td></tr>
              <tr><td style={{ color: '#888' }}>Frequency</td><td>{fmtFreq(drone.frequencyMhz)}</td></tr>
              <tr><td style={{ color: '#888' }}>Signal</td><td>{drone.signalDb?.toFixed(1) ?? '—'} dB</td></tr>
              <tr><td style={{ color: '#888' }}>Distance</td><td>{drone.distanceM ? formatDistance(drone.distanceM) : '—'}</td></tr>
              <tr><td style={{ color: '#888' }}>Tenant</td><td>{drone.tenantId || '—'}</td></tr>
            </tbody>
          </table>
        </div>
      </Popup>
    </Marker>
  );
}
